import React, { useMemo } from "react"
import { DateTime } from "luxon"
import { useStore } from "@/store"
import { useVenue } from "@/contexts/venue"
import { getHoursWrapped } from "@/components/calendar_view/_utils"
import i18n from "@/i18n"

type EmptyStateLayerProps = {
  tables: { id: string }[]
  startHour: number
  endHour: number
}

const DEFAULT_MIN_RESERVATION_SECONDS = 3600
const DEFAULT_MAX_RESERVATION_SECONDS = 10800
const SKELETON_COUNT = 4

const NoReservationsIcon = () => (
  <svg
    width="63"
    height="54"
    viewBox="0 0 62.5682 54"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M41.0432 1.00002C42.5565 1.00002 43.8119 2.04783 44.1291 3.43263L44.1799 3.71388L48.9387 39.8154V39.8164C48.9575 39.9653 48.968 40.0982 48.968 40.2276C48.9679 41.9407 47.5627 43.3466 45.8029 43.3467H4.16522C2.40543 43.3466 1.00024 41.9407 1.00018 40.2276C1.00018 40.1259 1.00925 40.023 1.02362 39.8731L4.02264 14.1172L4.02362 14.1123C4.3684 11.0249 6.68848 8.50848 9.71893 7.84181H9.71991L40.3527 1.0713C40.5832 1.02548 40.8148 1.00004 41.0432 1.00002Z"
      fill="#EEEEEE"
      stroke="#6B7280"
      strokeWidth="2"
    />
    <path
      d="M17.8389 44.1335C17.7209 44.1332 17.6042 44.1323 17.4889 44.1307C17.604 44.1316 17.7207 44.1325 17.8389 44.1335C27.6358 44.1529 46.6118 39.3301 45.6211 20.8953C44.9235 15.8069 44.3188 11.8133 44.1381 10.6363C44.0681 10.1804 44.0616 10.1471 44.1381 10.6363C44.2644 11.4452 44.6172 13.6826 45.2834 17.8009C45.4568 18.8727 45.5678 19.9037 45.6211 20.8953C46.5333 27.5488 47.6043 36.0741 48.0941 42.6217C47.7399 43.1966 46.7764 44.3463 45.7562 44.3463C44.7491 44.3463 26.9506 44.2062 17.8389 44.1335Z"
      fill="#806B6C"
      fillOpacity="0.1"
    />
    <path
      d="M62.0834 52.8116C61.6669 53.4151 61.0305 53.8266 60.3131 53.9564C59.5958 54.0862 58.8556 53.9238 58.254 53.5046L48.4387 46.6132C45.9646 49.0099 42.6943 50.3886 39.2623 50.4818C35.8302 50.575 32.4811 49.3761 29.8647 47.1175C27.2484 44.859 25.5513 41.702 25.1028 38.2589C24.6543 34.8159 25.4863 31.3324 27.4374 28.4842C29.3885 25.636 32.3196 23.6263 35.662 22.8449C39.0044 22.0635 42.5198 22.5662 45.5262 24.2555C48.5325 25.9447 50.8155 28.7 51.9321 31.9869C53.0487 35.2737 52.9193 38.8577 51.5691 42.0435L61.382 48.9354C61.9814 49.3589 62.3916 50.0042 62.523 50.7305C62.6544 51.4568 62.4964 52.205 62.0834 52.8116ZM44.7926 45.976C47.1337 44.4849 48.8378 42.1634 49.5726 39.4645C50.3073 36.7655 50.02 33.8825 48.7666 31.378C47.5132 28.8735 45.3837 26.927 42.7934 25.9182C40.2031 24.9094 37.3378 24.9106 34.7565 25.9217C32.1752 26.9328 30.0629 28.8812 28.8317 31.3868C27.6005 33.8924 27.3386 36.7756 28.0973 39.4739C28.8559 42.1722 30.5805 44.4922 32.9348 45.9813C35.2891 47.4703 38.1042 48.0217 40.8309 47.5278C42.2387 47.2753 43.5859 46.748 44.7926 45.976Z"
      fill="#6B7280"
    />
    <ellipse cx="38.401" cy="36.4386" rx="11.7848" ry="11.7848" fill="white" />
    <path d="M34.4757 31.7115L43.2977 40.5335L42.4957 41.3355L33.6737 32.5135L34.4757 31.7115Z" fill="#D9D9D9" />
    <path d="M43.2977 32.5135L34.743 41.0681L33.941 40.2661L42.4957 31.7115L43.2977 32.5135Z" fill="#D9D9D9" />
  </svg>
)

const SkeletonReservationCard: React.FC<{
  position: { top: number; left: number; width: number; height: number }
}> = ({ position }) => (
  <div
    className="absolute pointer-events-none"
    style={{
      top: position.top,
      left: position.left,
      width: position.width,
      height: position.height,
    }}
  >
    <div className="mx-1 h-full flex flex-col">
      <div className="h-[5px] rounded-t-[5px] bg-[#e5e5e6] border border-[#e5e5e6] border-solid border-b-0" />
      <div className="flex-1 rounded-b-[5px] bg-[#fbfbfb] border border-[#e5e5e6] border-solid border-t-0 p-[5px] flex flex-col gap-[4px]">
        <div className="h-[8px] w-full rounded-[3px] bg-[#f1f1f1]" />
        <div className="h-[8px] w-full rounded-[3px] bg-[#f1f1f1]" />
        <div className="h-[8px] w-[27%] rounded-[3px] bg-[#f1f1f1]" />
      </div>
    </div>
  </div>
)

const EmptyStateMessage: React.FC = () => (
  <div
    className="absolute inset-0 z-[10] flex items-center justify-center pointer-events-none"
    role="status"
    data-testid="panel-reservations-calendar-empty-state"
  >
    <div className="flex flex-col items-center justify-center gap-[10px] rounded-[10px] bg-white p-5 shadow-[4px_4px_4px_0px_rgba(0,0,0,0.1)]">
      <NoReservationsIcon />
      <p className="font-medium text-[16px] text-[#6b7280] whitespace-nowrap">
        {i18n.t("app.reservations_calendar.no_reservations")}
      </p>
    </div>
  </div>
)

const seededRandom = (seed: number) => {
  let s = seed
  return () => {
    s = (s * 16807 + 0) % 2147483647
    return (s - 1) / 2147483646
  }
}

type SkeletonPlacement = {
  tableId: string
  startQuarter: number
  durationQuarters: number
}

const generateSkeletonPlacements = (
  tables: { id: string }[],
  hours: number[],
  minSeconds: number,
  maxSeconds: number,
  dateSeed: number,
): SkeletonPlacement[] => {
  if (tables.length === 0 || hours.length === 0) return []

  const seed = dateSeed * 1000 + tables.length * 100 + hours.length
  const random = seededRandom(seed)

  const totalQuarters = hours.length * 4
  const minQuarters = Math.max(2, Math.round(minSeconds / 60 / 15))
  const maxQuarters = Math.min(totalQuarters - 2, Math.round(maxSeconds / 60 / 15))

  const count = Math.min(SKELETON_COUNT, tables.length)
  const placements: SkeletonPlacement[] = []
  const usedColumns = new Set<number>()

  for (let attempt = 0; attempt < count * 10 && placements.length < count; attempt++) {
    const tableIndex = Math.floor(random() * tables.length)
    if (usedColumns.has(tableIndex)) continue

    const durationQuarters = minQuarters + Math.floor(random() * (maxQuarters - minQuarters + 1))
    const maxStart = totalQuarters - durationQuarters
    if (maxStart <= 0) continue

    const startQuarter = 1 + Math.floor(random() * (maxStart - 1))

    usedColumns.add(tableIndex)
    placements.push({
      tableId: tables[tableIndex].id,
      startQuarter,
      durationQuarters,
    })
  }

  return placements
}

const EmptyStateLayer: React.FC<EmptyStateLayerProps> = ({ tables, startHour, endHour }) => {
  const { calendarDimensions, currentDate } = useStore()
  const { venue } = useVenue()

  const hours = useMemo(() => getHoursWrapped(startHour, endHour), [startHour, endHour])

  const minSeconds = venue.reservationConfiguration?.minReservationTime ?? DEFAULT_MIN_RESERVATION_SECONDS
  const maxSeconds = venue.reservationConfiguration?.maxReservationTime ?? DEFAULT_MAX_RESERVATION_SECONDS

  const dateSeed = currentDate ? currentDate.year * 10000 + currentDate.month * 100 + currentDate.day : 0

  const skeletonPositions = useMemo(() => {
    const placements = generateSkeletonPlacements(tables, hours, minSeconds, maxSeconds, dateSeed)

    return placements
      .map((placement) => {
        const startHourIndex = Math.floor(placement.startQuarter / 4)
        const startMinute = (placement.startQuarter % 4) * 15
        const endQuarter = placement.startQuarter + placement.durationQuarters
        const endHourIndex = Math.floor(endQuarter / 4)
        const endMinute = (endQuarter % 4) * 15

        const startHourValue = hours[startHourIndex]
        const endHourValue = hours[Math.min(endHourIndex, hours.length - 1)]

        if (startHourValue == null || endHourValue == null) return null

        const position = calendarDimensions.getReservationPosition({
          tableId: placement.tableId,
          startTime: DateTime.fromObject({ hour: startHourValue, minute: startMinute }),
          endTime: DateTime.fromObject({ hour: endHourValue, minute: endMinute }),
        })

        return position
      })
      .filter((p): p is NonNullable<typeof p> => p != null)
  }, [tables, hours, minSeconds, maxSeconds, dateSeed, calendarDimensions])

  return (
    <div
      className="absolute inset-0 z-[5] pointer-events-none"
      style={{
        width: calendarDimensions.canvas.width,
        height: calendarDimensions.canvas.height,
      }}
      data-testid="panel-reservations-calendar-empty-state-layer"
    >
      {skeletonPositions.map((position, index) => (
        <SkeletonReservationCard key={index} position={position} />
      ))}
      <EmptyStateMessage />
    </div>
  )
}

export default EmptyStateLayer
